/**
 * DocumentSplitEditor — Visual editor for Step 1 (document splitting).
 *
 * Displays PDF pages as a scrollable stack of thumbnails. Documents are
 * rendered as "sections" bracketing a range of pages, with draggable
 * dividers between them. The user adjusts document boundaries by:
 *   - Dragging a divider up/down
 *   - Using "Split here" on a page thumbnail (creates a new divider)
 *   - Using "Merge" on a divider (removes it)
 * Per-document metadata (title, type) is edited inline.
 *
 * Changes that affect page boundaries OR document_type trigger a
 * confirmation dialog before save, warning that the pipeline will
 * be re-run. Title-only changes save without a dialog.
 *
 * Props (from render_scan_data view='document_split'):
 *   data: { documents: [...], summary, warnings }  — the statement_id_result JSON
 *   schema: JSON Schema for display labels and enum labels
 *   artifactType: "statement_id_result"
 *   pdfBucket/pdfKey/pdfUrl: source PDF (the original uploaded PDF)
 *   onInteraction: callback for save_corrections, etc.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import useGetSignedUrl from '../../api/signedurl';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';
import PDFSidePanel from '../ui/pdf-side-panel';
import { getEnumLabel } from '../../utils/schemaUtils';
import { stableDocumentKeyFromUrl } from '../../utils/documentIdentity.js';
import { usePdfDocumentSource } from '../../utils/pdfDocumentSource.js';
import styles from './DocumentSplitEditor.module.css';

// Worker configured in PDFViewer — reuse same worker. See PDFViewer.js comment.
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

const PDF_DOCUMENT_OPTIONS = {
  cMapUrl: 'cmaps/',
  cMapPacked: true,
};

const THUMB_SCALE = 0.3;

function DocumentSplitEditor({ data: initialData, schema, artifactType, title, pdfUrl, pdfBucket, pdfKey, documentName = null, onInteraction }) {
  // Clone to avoid mutating parent state
  const [docs, setDocs] = useState(() => (initialData?.documents || []).map(d => ({ ...d })));
  const [numPages, setNumPages] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Page shown in the right-hand PDF viewer. Defaults to page 1 (institution + account
  // owner live there) so the reviewer can confirm immediately; thumbnail clicks update it.
  const [focusedPage, setFocusedPage] = useState(1);
  const dragStateRef = useRef(null); // { docIdx, startY, originalBoundary }

  // Get signed URL for PDF. SWR re-mints a URL for the SAME object every 4 minutes;
  // we pass the live one straight through. This used to be pinned to the first URL
  // (lockedUrlRef) to stop the PDF re-downloading on every render — but a pinned URL
  // is expired 15 minutes later, which silently broke the viewer's Download button
  // (it saved the S3 "Request has expired" XML as a .pdf). Re-downloading is now
  // prevented properly, one layer down: usePdfDocumentSource keys its cache on the
  // stable document identity and reads the URL from a ref, so URL churn alone never
  // re-fetches or re-parses. PDFSidePanel likewise keys its navigation on
  // stableDocumentKeyFromUrl, so a fresh signature does not reset the page.
  const { data: signedUrl } = useGetSignedUrl(pdfBucket, pdfKey, !!(pdfBucket && pdfKey && !pdfUrl));
  const effectivePdfUrl = pdfUrl || signedUrl;

  // Stable Document file prop — never changes after initial load. Bytes come
  // from the shared pdfDocumentSource cache, so the thumbnail tree and the
  // side-panel PdfViewer below fetch the S3 PDF once instead of twice (and a
  // remount mid-load can't destroy an in-flight pdf.js task — the old silent
  // "Loading PDF..." hang).
  const {
    file: pdfFileProp,
    error: pdfError,
    retry: retryPdfLoad,
  } = usePdfDocumentSource(effectivePdfUrl ? stableDocumentKeyFromUrl(effectivePdfUrl) : null, effectivePdfUrl);

  // Schema lookups for the document type field
  const docTypeSchema = schema?.properties?.documents?.items?.properties?.document_type;
  const docTypeEnum = docTypeSchema?.enum || [];

  // --- Derived state: detect changes ---
  const { hasBoundaryOrTypeChange, hasAnyChange, changedCount } = useMemo(() => {
    const original = initialData?.documents || [];
    if (docs.length !== original.length) {
      return { hasBoundaryOrTypeChange: true, hasAnyChange: true, changedCount: docs.length };
    }
    let btCount = 0;
    let anyCount = 0;
    for (let i = 0; i < docs.length; i++) {
      const d = docs[i];
      const o = original[i] || {};
      const boundaryChanged = d.start_page !== o.start_page || d.end_page !== o.end_page;
      const typeChanged = d.document_type !== o.document_type;
      const titleChanged = (d.title || '') !== (o.title || '');
      if (boundaryChanged || typeChanged) btCount++;
      if (boundaryChanged || typeChanged || titleChanged) anyCount++;
    }
    return { hasBoundaryOrTypeChange: btCount > 0, hasAnyChange: anyCount > 0, changedCount: btCount };
  }, [docs, initialData]);

  // --- Document operations ---
  const updateDoc = useCallback((idx, patch) => {
    setDocs(prev => prev.map((d, i) => i === idx ? { ...d, ...patch } : d));
  }, []);

  // Set end_page of doc[idx] to a page, and start_page of doc[idx+1] to page+1
  const setBoundaryAfter = useCallback((docIdx, newEndPage) => {
    setDocs(prev => {
      if (docIdx >= prev.length - 1) return prev;
      const next = [...prev];
      const cur = next[docIdx];
      const after = next[docIdx + 1];
      // Clamp: boundary must be within valid range
      const minBoundary = cur.start_page;
      const maxBoundary = after.end_page - 1;
      const clamped = Math.max(minBoundary, Math.min(maxBoundary, newEndPage));
      next[docIdx] = { ...cur, end_page: clamped, page_count: clamped - cur.start_page + 1 };
      next[docIdx + 1] = { ...after, start_page: clamped + 1, page_count: after.end_page - clamped };
      return next;
    });
  }, []);

  // Split: insert a new divider before page `pageNum` (page becomes start of new doc)
  const splitAtPage = useCallback((pageNum) => {
    setDocs(prev => {
      // Find the document containing pageNum
      const idx = prev.findIndex(d => pageNum > d.start_page && pageNum <= d.end_page);
      if (idx < 0) return prev;
      const cur = prev[idx];
      const next = [...prev];
      // Current doc becomes pages start_page → pageNum-1
      next[idx] = { ...cur, end_page: pageNum - 1, page_count: pageNum - cur.start_page };
      // New doc is pageNum → original end_page
      const maxDocNum = Math.max(...prev.map(d => d.doc_number || 0));
      const newDoc = {
        doc_number: maxDocNum + 1,
        original_doc_number: maxDocNum + 1,
        document_type: cur.document_type,
        title: cur.title || '',
        start_page: pageNum,
        end_page: cur.end_page,
        page_count: cur.end_page - pageNum + 1,
        confidence: 'high', // user-set
        reasoning: 'Split by reviewer',
        institution_or_org: cur.institution_or_org,
        date_or_period: cur.date_or_period,
        identifiers: {},
        sub_sections: [],
      };
      next.splice(idx + 1, 0, newDoc);
      return next;
    });
  }, []);

  // Merge: remove divider between docIdx and docIdx+1
  const mergeWithNext = useCallback((docIdx) => {
    setDocs(prev => {
      if (docIdx >= prev.length - 1) return prev;
      const next = [...prev];
      const cur = next[docIdx];
      const after = next[docIdx + 1];
      next[docIdx] = {
        ...cur,
        end_page: after.end_page,
        page_count: after.end_page - cur.start_page + 1,
        reasoning: 'Merged by reviewer',
      };
      next.splice(docIdx + 1, 1);
      return next;
    });
  }, []);

  // Fold a run of unassigned pages into the document directly above ('above' ->
  // extend that doc's end_page) or below ('below' -> extend its start_page). This
  // is how a reviewer recovers pages a premature statement-ID split left uncovered.
  const extendDocOverGap = useCallback((targetDocIdx, side, gapStart, gapEnd) => {
    setDocs(prev => {
      const doc = prev[targetDocIdx];
      if (!doc) return prev;
      const next = [...prev];
      if (side === 'above') {
        const newEnd = Math.max(doc.end_page, gapEnd);
        next[targetDocIdx] = { ...doc, end_page: newEnd, page_count: newEnd - doc.start_page + 1, reasoning: 'Pages added by reviewer' };
      } else {
        const newStart = Math.min(doc.start_page, gapStart);
        next[targetDocIdx] = { ...doc, start_page: newStart, page_count: doc.end_page - newStart + 1, reasoning: 'Pages added by reviewer' };
      }
      return next;
    });
  }, []);

  // Make a brand-new document out of a run of unassigned pages (used when the gap
  // has no adjacent document to absorb it — e.g. leading pages before the first doc).
  const createDocFromGap = useCallback((gapStart, gapEnd) => {
    setDocs(prev => {
      const maxDocNum = prev.length ? Math.max(...prev.map(d => d.doc_number || 0)) : 0;
      const newDoc = {
        doc_number: maxDocNum + 1,
        original_doc_number: maxDocNum + 1,
        document_type: '',
        title: '',
        start_page: gapStart,
        end_page: gapEnd,
        page_count: gapEnd - gapStart + 1,
        confidence: 'high',
        reasoning: 'Created by reviewer',
        institution_or_org: null,
        date_or_period: null,
        identifiers: {},
        sub_sections: [],
      };
      return [...prev, newDoc].sort((a, b) => a.start_page - b.start_page);
    });
  }, []);

  // --- Drag handlers for dividers ---
  const handleDividerMouseDown = useCallback((e, docIdx) => {
    e.preventDefault();
    dragStateRef.current = { docIdx, startY: e.clientY, originalEnd: docs[docIdx].end_page };
    document.body.style.cursor = 'ns-resize';

    const handleMove = (ev) => {
      // Approximate: ~thumbnail height to map drag distance to page changes.
      // Simple heuristic — each ~80px of drag moves the boundary by one page.
      const state = dragStateRef.current;
      if (!state) return;
      const delta = ev.clientY - state.startY;
      // Invert: dragging the divider DOWN should move the boundary DOWN
      // (more pages into the current doc = larger end_page). If this feels
      // wrong in the UI, try flipping the sign here.
      const pageDelta = -Math.round(delta / 80);
      if (pageDelta !== 0) {
        setBoundaryAfter(state.docIdx, state.originalEnd + pageDelta);
      }
    };

    const handleUp = () => {
      dragStateRef.current = null;
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [docs, setBoundaryAfter]);

  // --- Save flow ---
  const handleSaveClick = useCallback(() => {
    if (hasBoundaryOrTypeChange) {
      setShowConfirm(true);
    } else {
      performSave(false);
    }
  }, [hasBoundaryOrTypeChange]);

  const performSave = useCallback((withRetrigger) => {
    setShowConfirm(false);
    if (!onInteraction) return;
    setSaving(true);
    // Build the output data matching the schema
    const outputData = {
      ...initialData,
      documents: docs,
    };
    onInteraction({
      type: 'save_corrections',
      data: outputData,
      artifactType: artifactType || 'statement_id_result',
      draft: false,
      retriggerPipeline: withRetrigger,
    });
    setTimeout(() => setSaving(false), 800);
  }, [docs, initialData, artifactType, onInteraction]);

  // --- Build page-to-document mapping for rendering ---
  const pageToDocIdx = useMemo(() => {
    const map = new Map();
    docs.forEach((d, idx) => {
      for (let p = d.start_page; p <= d.end_page; p++) {
        map.set(p, idx);
      }
    });
    return map;
  }, [docs]);

  // Ordered render model over the WHOLE original PDF (pages 1..numPages), not just the
  // pages statement-ID assigned to a document. Consecutive pages that belong to a
  // document form a 'doc' block; any run of pages no document claims forms a 'gap'
  // block. Rendering gaps is what makes a premature split recoverable: the pages
  // statement-ID dropped (e.g. it truncated a statement at an interstitial page) are
  // visible and can be added back, instead of silently vanishing from the review.
  const renderBlocks = useMemo(() => {
    if (!numPages) return [];
    const blocks = [];
    let p = 1;
    while (p <= numPages) {
      const idx = pageToDocIdx.get(p);
      if (idx === undefined) {
        const startPage = p;
        while (p <= numPages && pageToDocIdx.get(p) === undefined) p++;
        blocks.push({ type: 'gap', startPage, endPage: p - 1 });
      } else {
        blocks.push({ type: 'doc', docIdx: idx });
        p = Math.max(docs[idx]?.end_page ?? p, p) + 1;
      }
    }
    return blocks;
  }, [numPages, pageToDocIdx, docs]);

  const unassignedCount = useMemo(
    () => renderBlocks.reduce((n, b) => b.type === 'gap' ? n + (b.endPage - b.startPage + 1) : n, 0),
    [renderBlocks]
  );

  // Render a color for each document section (alternating)
  const docColor = (idx) => idx % 2 === 0 ? styles.docEven : styles.docOdd;

  if (!effectivePdfUrl) {
    return <div className={styles.loading}>Loading PDF…</div>;
  }

  // The split-editor UI (toolbar + thumbnails) becomes the LEFT pane; a readable,
  // zoomable PDF viewer is shown on the RIGHT via the shared PDFSidePanel. Clicking a
  // thumbnail sets focusedPage, which drives the viewer's page (Trello #727 — reviewers
  // need a legible view to confirm institution + account owner). Defaults to page 1.
  const editorPanel = (
      <div className={styles.container}>
        {/* Toolbar */}
        <div className={styles.topBar}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{title || 'Document Split Editor'}</h3>
            <span className={styles.docCount}>
              {docs.length} document(s) · {numPages || '?'} pages
              {unassignedCount > 0 && <span className={styles.unassignedCount}> · {unassignedCount} unassigned</span>}
            </span>
          </div>
          <div className={styles.actions}>
            {hasAnyChange && (
              <span className={styles.changeBadge}>
                {hasBoundaryOrTypeChange
                  ? `${changedCount} change(s) will re-run pipeline`
                  : 'Title changes only'}
              </span>
            )}
            <button
              className={styles.saveBtn}
              onClick={handleSaveClick}
              disabled={saving || !hasAnyChange}
            >
              {saving ? 'Saving…' : (hasBoundaryOrTypeChange ? 'Save & Re-run' : 'Save')}
            </button>
          </div>
        </div>

        {/* Page list with documents as groups */}
        <div className={styles.scrollArea}>
          <Document
            file={pdfFileProp}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className={styles.loading}>Loading PDF…</div>}
            error={
              <div className={styles.error}>
                Failed to load PDF
                {pdfError && <div className={styles.errorDetail}>{pdfError.message || pdfError}</div>}
                <button className={styles.retryBtn} onClick={retryPdfLoad}>Retry</button>
              </div>
            }
          >
            {numPages > 0 && renderBlocks.map((block) => {
              if (block.type === 'gap') {
                const { startPage, endPage } = block;
                const prevDocIdx = docs.findIndex(d => d.end_page === startPage - 1);
                const nextDocIdx = docs.findIndex(d => d.start_page === endPage + 1);
                const label = startPage === endPage ? `Page ${startPage}` : `Pages ${startPage}–${endPage}`;
                return (
                  <div key={`gap-${startPage}`} className={styles.gapSection}>
                    <div className={styles.gapHeader}>
                      <span className={styles.gapLabel}>⚠ Unassigned · {label}</span>
                      <span className={styles.gapHint}>Not part of any document — add these pages back where they belong.</span>
                      <div className={styles.gapActions}>
                        {prevDocIdx >= 0 && (
                          <button
                            className={styles.assignBtn}
                            onClick={() => extendDocOverGap(prevDocIdx, 'above', startPage, endPage)}
                            title={`Add these pages to Doc ${docs[prevDocIdx].doc_number} above`}
                          >↑ Add to Doc {docs[prevDocIdx].doc_number}</button>
                        )}
                        {nextDocIdx >= 0 && (
                          <button
                            className={styles.assignBtn}
                            onClick={() => extendDocOverGap(nextDocIdx, 'below', startPage, endPage)}
                            title={`Add these pages to Doc ${docs[nextDocIdx].doc_number} below`}
                          >↓ Add to Doc {docs[nextDocIdx].doc_number}</button>
                        )}
                        {prevDocIdx < 0 && nextDocIdx < 0 && (
                          <button
                            className={styles.assignBtn}
                            onClick={() => createDocFromGap(startPage, endPage)}
                            title="Make a new document from these pages"
                          >＋ New document</button>
                        )}
                      </div>
                    </div>
                    <div className={styles.thumbRow}>
                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                        <div
                          key={pageNum}
                          className={`${styles.thumb} ${focusedPage === pageNum ? styles.thumbFocused : ''}`}
                          onClick={() => setFocusedPage(pageNum)}
                          title={`Page ${pageNum}`}
                        >
                          <div className={styles.pageBadge}>{pageNum}</div>
                          <Page
                            pageNumber={pageNum}
                            scale={THUMB_SCALE}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={<div className={styles.thumbLoading}>…</div>}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              const docIdx = block.docIdx;
              const doc = docs[docIdx];
              const pageCount = doc.end_page - doc.start_page + 1;
              const nextDoc = docs[docIdx + 1];
              const nextDocAdjacent = nextDoc && nextDoc.start_page === doc.end_page + 1;
              return (
                <div key={`doc-${docIdx}`} className={`${styles.docSection} ${docColor(docIdx)}`}>
                  {/* Document header */}
                  <div className={styles.docHeader}>
                    <span className={styles.docNumber}>Doc {doc.doc_number}</span>
                    <input
                      type="text"
                      className={styles.titleInput}
                      placeholder="Title"
                      value={doc.title || ''}
                      onChange={(e) => updateDoc(docIdx, { title: e.target.value })}
                    />
                    <select
                      className={styles.typeSelect}
                      value={doc.document_type || ''}
                      onChange={(e) => updateDoc(docIdx, { document_type: e.target.value })}
                    >
                      {docTypeEnum.map(v => (
                        <option key={v} value={v}>{getEnumLabel(docTypeSchema, v)}</option>
                      ))}
                    </select>
                    <span className={styles.pageRange}>
                      Pages {doc.start_page}–{doc.end_page} ({pageCount})
                    </span>
                  </div>

                  {/* Page thumbnails for this document */}
                  <div className={styles.thumbRow}>
                    {Array.from({ length: pageCount }, (_, i) => doc.start_page + i).map(pageNum => (
                      <div
                        key={pageNum}
                        className={`${styles.thumb} ${focusedPage === pageNum ? styles.thumbFocused : ''}`}
                        onClick={() => setFocusedPage(pageNum)}
                        title={`Page ${pageNum}`}
                      >
                        <div className={styles.pageBadge}>{pageNum}</div>
                        <Page
                          pageNumber={pageNum}
                          scale={THUMB_SCALE}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={<div className={styles.thumbLoading}>…</div>}
                        />
                        {pageNum !== doc.start_page && (
                          <button
                            className={styles.splitHereBtn}
                            onClick={(e) => { e.stopPropagation(); splitAtPage(pageNum); }}
                            title={`Start a new document at page ${pageNum}`}
                          >✂ Split here</button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Draggable boundary — only between two DIRECTLY adjacent documents
                      (a gap block handles the space when they are not adjacent). */}
                  {nextDocAdjacent && (
                    <div className={styles.divider}>
                      <div
                        className={styles.dividerHandle}
                        onMouseDown={(e) => handleDividerMouseDown(e, docIdx)}
                      >
                        ↕ Drag to move boundary
                      </div>
                      <button
                        className={styles.mergeBtn}
                        onClick={() => mergeWithNext(docIdx)}
                        title="Remove this divider and merge with next document"
                      >× Merge with next</button>
                    </div>
                  )}
                </div>
              );
            })}
          </Document>
        </div>
      </div>
  );

  return (
    <>
      <PDFSidePanel
        fileUrl={effectivePdfUrl}
        documentName={documentName}
        displayPage={focusedPage || 1}
        leftChild={editorPanel}
      />

      <ConfirmBeforeActionDialog
        open={showConfirm}
        title="Re-run OCR Pipeline"
        confirmMessage={`Saving these changes will re-run the OCR pipeline for this scan.\n\n• ${changedCount} document(s) will be re-processed\n• The current HIL session will be marked as superseded\n• New HIL sessions may be created if quality checks fail\n\nContinue?`}
        proceedButtonLabel="Save & Re-run Pipeline"
        cancelButtonLabel="Cancel"
        onProceed={() => performSave(true)}
        onCancel={() => setShowConfirm(false)}
        list_element={() => null}
      />
    </>
  );
}

export default DocumentSplitEditor;
